import {Box, Button, CircularProgress, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {SurveyAnswer, SurveyMediaValue} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {
    RunnerQuestionServices,
    RunnerSignatureCapture,
    SurveyAnswerDraft,
    SurveyRunnerLocalize
} from "../runner/questions/runnerQuestion.types";
import SurveyRunnerIntroCard from "../runner/SurveyRunnerIntroCard";
import SurveyRunnerMarkdown from "../runner/SurveyRunnerMarkdown";
import SurveyRunnerQuestionCard from "../runner/SurveyRunnerQuestionCard";
import SurveyRunnerScreenTitle from "../runner/SurveyRunnerScreenTitle";
import SurveyRunnerSectionHeading from "../runner/SurveyRunnerSectionHeading";
import {
    isPagedFormat,
    questionInFront,
    SurveyPage,
    surveyPages,
    surveyProgress,
    visiblePages
} from "../shared/surveyPages.util";
import {surveyMarkdownToPlainText} from "../shared/surveyMarkdownText.util";
import {isScreen} from "../shared/surveyQuestions.util";
import {visibleSurveyQuestions} from "../shared/surveyVisibility.util";
import {
    anonymousIdForSurvey,
    getAnonymousSurvey,
    getInvitationSurvey,
    isPublicSurveyFailure,
    PublicSurveyFailure,
    PublicSurveyIdentity,
    PublicSurveyPayload,
    rememberedLanguage,
    rememberLanguage,
    submitPublicSurvey,
    uploadPublicSurveyMedia,
    uploadPublicSurveySignature
} from "./publicSurvey.api";
import {dropDraft, dropStaleDrafts, restoreDraft, saveDraft} from "./publicSurveyDraft";
import PublicSurveyFooter from "./PublicSurveyFooter";
import PublicSurveyFrame from "./PublicSurveyFrame";
import PublicSurveyNotice from "./PublicSurveyNotice";
import {LOADING_PHASE, payloadOf, publicSurveyPhaseReducer} from "./publicSurveyPhase";
import {
    isAnswered,
    SurveyFieldProblem,
    surveyAnswerProblem,
    surveyAnswerProblems
} from "../shared/surveyAnswerValidation.util";
import {problemFromServerError} from "./publicSurveyValidation.util";
import SurveySuccessAnimation from "./SurveySuccessAnimation";
import usePublicSurveySession from "./usePublicSurveySession";

type Answers = Record<string, SurveyAnswerDraft | undefined>;
type Problems = Record<string, SurveyFieldProblem>;

const REDIRECT_DEFAULT_SECONDS = 5;

const redirectTarget = (redirectUrl: string | undefined): string | undefined => {
    const raw = redirectUrl?.trim();
    if (!raw) return undefined;

    try {
        const url = new URL(raw);
        return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : undefined;
    } catch {
        return undefined;
    }
};

const browserLanguages = (): string[] => {
    if (typeof navigator === 'undefined') return [];

    const raw = navigator.languages?.length ? navigator.languages : [navigator.language];
    return raw.filter(Boolean).map(tag => tag.split('-')[0].toLowerCase());
};

const pickLanguage = (payload: PublicSurveyPayload, remembered: string | undefined): string => {
    const offered = payload.languages ?? [];

    /*
        A pick at the flag on this same link outranks the profile and the browser. It also carries the answered-in
        language onto the short "already answered" payload, which names no languages at all.
    */
    if (remembered && (!offered.length || offered.includes(remembered))) return remembered;

    if (!offered.length) return browserLanguages()[0] ?? 'en';

    const guest = payload.guestLanguage?.toLowerCase();
    if (guest && offered.includes(guest)) return guest;

    return browserLanguages().find(tag => offered.includes(tag)) ?? offered[0];
};

const answeredOnly = (answers: Answers): Answers => {
    const given: Answers = {};

    Object.entries(answers).forEach(([questionId, draft]) => {
        if (isAnswered(draft)) given[questionId] = draft;
    });

    return given;
};

const toSubmittedAnswers = (questions: SurveyQuestion[], answers: Answers): SurveyAnswer[] => questions
    .filter(question => !!question.id && isAnswered(answers[question.id]))
    .map(question => {
        const draft = answers[question.id!]!;

        return {
            questionId: question.id,
            type: question.type,
            textValue: draft.textValue?.trim() || undefined,
            scaleValue: draft.scaleValue,
            rateValue: draft.rateValue,
            numberValue: draft.numberValue,
            dateValue: draft.dateValue || undefined,
            booleanValue: draft.booleanValue,
            optionIds: draft.optionIds?.length ? draft.optionIds : undefined,
            customText: draft.customText?.trim() || undefined,
            mediaValues: draft.mediaValues?.length ? draft.mediaValues : undefined,
            signatureValue: draft.signatureValue,
        };
    });

/*
    The first control of a card's answer area, in tab order. Focus after a failed Continue belongs on the answer:
    never on the help button in the title, and never on the hidden helper inputs MUI renders beside its fields.
    The preview (runner/SurveyRunner.tsx) carries the same helper, and public/surveyRunnersParity.test.tsx holds
    both to this rule — change one and change this.
*/
const firstAnswerControl = (card: HTMLElement): HTMLElement | undefined => {
    const area = card.querySelector('[data-survey-answer]');
    if (!area) return undefined;

    return Array.from(area.querySelectorAll<HTMLElement>('input, select, textarea, button, [tabindex]'))
        .find(control => control.tabIndex >= 0
            && !control.hasAttribute('disabled')
            && control.getAttribute('aria-hidden') !== 'true'
            && control.getAttribute('type') !== 'hidden');
};

function PublicSurvey({surveyId, token}: {surveyId?: string, token?: string}) {

    const [phase, dispatch] = useReducer(publicSurveyPhaseReducer, LOADING_PHASE);
    // One object from the load to the end screen, so the memos and effects keyed on it hold still across phases.
    const payload = payloadOf(phase);
    const submitted = phase.kind === 'submitted';

    const [language, setLanguage] = useState<string | undefined>(undefined);
    const [answers, setAnswers] = useState<Answers>({});
    const [problems, setProblems] = useState<Problems>({});

    const [started, setStarted] = useState(false);
    const [pageKey, setPageKey] = useState<string | undefined>(undefined);
    // Start, an answer, Continue or Submit (the last page's Continue); until then the session names no question.
    const [acted, setActed] = useState(false);
    const [focusRequest, setFocusRequest] = useState<{questionId: string} | undefined>(undefined);

    const answersRef = useRef<Answers>(answers);
    answersRef.current = answers;

    const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const signatureCapturesRef = useRef<Record<string, RunnerSignatureCapture | undefined>>({});

    const identity: PublicSurveyIdentity = useMemo(() => (
        token
            ? {token}
            : {surveyId, anonymousId: surveyId ? anonymousIdForSurvey(surveyId) : undefined}
    ), [token, surveyId]);

    const identityRef = useRef(identity);
    identityRef.current = identity;

    const {sessionIdRef, track} = usePublicSurveySession({identity, version: payload?.version});

    const {t} = useTranslation(undefined, {lng: language});

    useEffect(() => {
        let cancelled = false;

        dispatch({type: 'loadStarted'});

        const load = token
            ? getInvitationSurvey(token)
            : surveyId
                ? getAnonymousSurvey(surveyId)
                : Promise.reject<PublicSurveyPayload>({kind: 'NOT_FOUND'} as PublicSurveyFailure);

        load
            .then(next => {
                if (cancelled) return;

                dropStaleDrafts();
                // Set before the survey shows: UploadQuestion builds its rows from the first answers it is given.
                const draft = next.alreadySubmitted ? undefined : restoreDraft(identityRef.current, next.version);
                if (draft) {
                    setAnswers(draft.answers);
                    setPageKey(draft.pageKey);
                    setStarted(draft.started);
                }

                dispatch({type: 'loaded', payload: next});
                setLanguage(pickLanguage(next, rememberedLanguage(identityRef.current)));
            })
            .catch(error => {
                if (cancelled) return;
                dispatch({type: 'loadFailed', failure: isPublicSurveyFailure(error) ? error : {kind: 'UNKNOWN'}});
            });

        return () => {
            cancelled = true;
        };
    }, [surveyId, token]);

    // The tab follows the chosen language: the welcome title in it, else the survey's name, which has no translations.
    const welcomeTitle = payload?.questions?.find(question => question.type === SurveyQuestionType.WELCOME)?.title;
    const documentTitle = surveyMarkdownToPlainText(language ? welcomeTitle?.[language] : undefined) || payload?.name;

    useEffect(() => {
        if (!documentTitle) return;

        const previous = document.title;
        document.title = documentTitle;

        return () => {
            document.title = previous;
        };
    }, [documentTitle]);

    // A pick at the flag is the guest's own word on the matter, so it outlives this page view - on this link.
    const chooseLanguage = useCallback((next: string) => {
        setLanguage(next);
        rememberLanguage(identityRef.current, next);
    }, []);

    /*
        The runner is the whole document on this route, so the page's language is the survey's -
        screen readers and the browser's own translation prompt both read it from here. Restored on
        unmount because the dashboard mounts this page inside its own shell in preview.
    */
    useEffect(() => {
        if (!language) return;

        const root = document.documentElement;
        const previous = root.lang;
        root.lang = language;

        return () => {
            root.lang = previous;
        };
    }, [language]);

    const localize: SurveyRunnerLocalize = useMemo(() => (text?: LocalizedText) => (
        language ? text?.[language] ?? '' : ''
    ), [language]);

    const questions = useMemo(() => payload?.questions ?? [], [payload]);
    const welcome = questions.find(question => question.type === SurveyQuestionType.WELCOME);
    const endScreen = questions.find(question => question.type === SurveyQuestionType.END_SCREEN);
    const answerable = useMemo(() => questions.filter(question => !isScreen(question)), [questions]);

    // Over the full list, not page by page: a rule may point at a question on another page.
    const visible = useMemo(
        () => visibleSurveyQuestions(answerable, answeredOnly(answers), payload?.guestCategoryIds ?? undefined),
        [answerable, answers, payload?.guestCategoryIds]
    );

    // The chrome follows the format, never the number of pages, so a page emptying out never switches the layout.
    const paged = isPagedFormat(payload?.format);

    /*
        Pages are formed once from the author's list, and the guest's visibility only thins them out: a conditional
        question that appears shows up in place on its own page and never pushes another onto the next one.
    */
    const pages = useMemo(() => surveyPages(questions, payload?.format), [questions, payload?.format]);
    const shown = useMemo(() => {
        const visibleSet = new Set(visible);
        return visiblePages(pages, question => visibleSet.has(question));
    }, [pages, visible]);

    /*
        The guest's position is a page key from the author's list, not an index into the shown pages: those shift
        whenever a conditional question comes or goes, and an index then quietly points at another page. A page that
        empties out under the guest hands the position on to the next shown page after it, or else to the last one.
    */
    const currentIndex = useMemo(() => {
        if (!shown.length) return -1;
        if (pageKey === undefined) return 0;

        const at = shown.findIndex(page => page.key === pageKey);
        if (at >= 0) return at;

        const authorIndexOf = (key: string) => pages.findIndex(page => page.key === key);
        const after = shown.findIndex(page => authorIndexOf(page.key) > authorIndexOf(pageKey));
        return after >= 0 ? after : shown.length - 1;
    }, [pages, shown, pageKey]);
    const currentPage: SurveyPage | undefined = shown[currentIndex];

    /*
        A layout effect, so the move lands with the render that found the page empty: a passive one can run late,
        after the guest's next tap, and put them back on the page they had just left.
    */
    useLayoutEffect(() => {
        if (!currentPage || currentPage.key === pageKey) return;

        // A move the guest did not ask for still lands on a new page, which starts at its top like any other.
        if (pageKey !== undefined) window.scrollTo({top: 0, behavior: 'auto'});
        setPageKey(currentPage.key);
    }, [currentPage, pageKey]);

    const setAnswer = useCallback((questionId: string, draft: SurveyAnswerDraft) => {
        setActed(true);
        setAnswers(current => ({...current, [questionId]: draft}));

        setProblems(current => {
            if (!current[questionId]) return current;

            const next = {...current};
            delete next[questionId];
            return next;
        });
    }, []);

    const services: RunnerQuestionServices = useMemo(() => ({
        uploadMedia: (questionId, file, onProgress) => (
            uploadPublicSurveyMedia(identityRef.current, questionId, file, onProgress)
        ),
        registerSignatureCapture: (questionId, capture) => {
            signatureCapturesRef.current[questionId] = capture;
        },
    }), []);

    const drainSignatures = useCallback(async (): Promise<Record<string, SurveyMediaValue>> => {
        const pending = Object.entries(signatureCapturesRef.current)
            .filter(([, capture]) => !!capture)
            .map(([questionId, capture]) => ({questionId, capturing: capture!()}));

        const captured: Record<string, SurveyMediaValue> = {};

        await Promise.all(pending.map(async ({questionId, capturing}) => {
            try {
                const imageDataUrl = await capturing;
                if (!imageDataUrl) return;

                captured[questionId] = await uploadPublicSurveySignature(
                    identityRef.current,
                    questionId,
                    imageDataUrl
                );
            } catch {
            }
        }));

        if (Object.keys(captured).length) {
            setAnswers(current => {
                const next = {...current};
                Object.entries(captured).forEach(([questionId, media]) => {
                    next[questionId] = {...next[questionId], signatureValue: media, signatureDrawn: true};
                });
                return next;
            });
        }

        return captured;
    }, []);

    const messageFor = (question: SurveyQuestion): string | undefined => {
        const problem = question.id ? problems[question.id] : undefined;
        return problem ? t(problem.key, problem.values) : undefined;
    };

    /*
        Only for an answer the guest has given - an invalid email, a number out of range. A missing answer is the
        required asterisk's to show until a tap on Continue or Submit asks for it.
    */
    const hintFor = (question: SurveyQuestion): string | undefined => {
        const draft = question.id ? answers[question.id] : undefined;
        if (!isAnswered(draft)) return undefined;

        const problem = surveyAnswerProblem(question, draft);
        return problem ? t(problem.key, problem.values) : undefined;
    };

    // Runs as soon as the page holding the problem is in the DOM, so the card and its controls are there to reach.
    useLayoutEffect(() => {
        const card = focusRequest ? cardRefs.current[focusRequest.questionId] : undefined;
        if (!card) return;

        card.scrollIntoView({block: 'center', behavior: 'smooth'});
        firstAnswerControl(card)?.focus({preventScroll: true});
    }, [focusRequest]);

    // Takes the guest to a problem: to its page in the paged formats, then to its card and its first control.
    const showProblem = (question: SurveyQuestion) => {
        if (!question.id) return;

        const page = paged ? pages.find(entry => entry.questions.includes(question)) : undefined;
        if (page) setPageKey(page.key);

        setFocusRequest({questionId: question.id});
    };

    const objectTo = (found: Problems, inQuestions: SurveyQuestion[]) => {
        setProblems(found);

        const first = inQuestions.find(question => !!question.id && !!found[question.id]);
        if (first) showProblem(first);
    };

    const goToPage = (key: string) => {
        setPageKey(key);
        window.scrollTo({top: 0, behavior: 'auto'});
    };

    const goBack = () => {
        void drainSignatures();

        if (currentIndex > 0) goToPage(shown[currentIndex - 1].key);
        else setStarted(false);
    };

    /*
        Never greyed out: with five questions on a page the missing one is often scrolled away, and a grey button
        reads as broken. A tap names what is missing and takes the guest to it instead.
    */
    const goNext = () => {
        setActed(true);
        if (!currentPage) return;

        const found = surveyAnswerProblems(currentPage.questions, answers);
        const first = currentPage.questions.find(question => !!question.id && !!found[question.id]);

        if (first) {
            setProblems(current => ({...current, ...found}));
            showProblem(first);
            return;
        }

        void drainSignatures();

        const next = shown[currentIndex + 1];
        if (next) goToPage(next.key);
    };

    const submit = async () => {
        if (!payload) return;

        setActed(true);
        dispatch({type: 'submitStarted'});

        const captured = await drainSignatures();
        const merged: Answers = {...answersRef.current};
        Object.entries(captured).forEach(([questionId, media]) => {
            merged[questionId] = {...merged[questionId], signatureValue: media, signatureDrawn: true};
        });

        const visibleNow = visibleSurveyQuestions(
            answerable,
            answeredOnly(merged),
            payload.guestCategoryIds ?? undefined
        );
        const found = surveyAnswerProblems(visibleNow, merged);

        if (Object.keys(found).length) {
            objectTo(found, visibleNow);
            dispatch({type: 'submitStopped'});
            return;
        }

        try {
            await submitPublicSurvey({
                ...identity,
                sessionId: sessionIdRef.current,
                version: payload.version,
                language,
                answers: toSubmittedAnswers(visibleNow, merged),
            });
            // Pins the language this fill-out used, so a return visit to the link is greeted in it.
            if (language) rememberLanguage(identity, language);
            dispatch({type: 'submitSucceeded'});
        } catch (error) {
            const failure: PublicSurveyFailure = isPublicSurveyFailure(error) ? error : {kind: 'UNKNOWN'};

            if (failure.kind === 'VALIDATION' && failure.errors?.length) {
                const fromServer: Problems = {};
                failure.errors.forEach(entry => {
                    if (entry.questionId) fromServer[entry.questionId] = problemFromServerError(entry);
                });

                objectTo(fromServer, visibleNow);
                dispatch({type: 'submitStopped', failure: Object.keys(fromServer).length ? undefined : failure});
            } else {
                dispatch({type: 'submitStopped', failure});
            }
        }
    };

    /*
        One progress rule for every format. Opening the link only creates the session, which the API counts as
        Opened; from the guest's first act it follows the question in front of them - the one after the furthest
        answered on their page - by its author index. The hook keeps forward moves only.
    */
    const inFront = currentPage
        ? questionInFront(currentPage, question => !!question.id && isAnswered(answers[question.id]))
        : undefined;
    const inFrontIndex = inFront ? answerable.indexOf(inFront) : -1;

    useEffect(() => {
        if (!payload || payload.alreadySubmitted) return;
        track(undefined, -1);
    }, [payload, track]);

    useEffect(() => {
        if (!payload || payload.alreadySubmitted || submitted || !acted) return;
        track(inFront?.id, inFrontIndex);
    }, [payload, submitted, acted, track, inFront, inFrontIndex]);

    // The device keeps the unsent answers from the guest's first act until they are sent (publicSurveyDraft.ts).
    useEffect(() => {
        if (phase.kind === 'submitted' || phase.kind === 'alreadyAnswered') {
            dropDraft(identity);
        } else if (phase.kind === 'answering' && acted) {
            saveDraft(identity, phase.payload.version, {answers, pageKey, started});
        }
    }, [phase, acted, identity, answers, pageKey, started]);

    const redirectTo = useMemo(() => redirectTarget(endScreen?.redirectUrl), [endScreen?.redirectUrl]);
    const redirectWaitSeconds = endScreen?.redirectWaitSeconds;

    useEffect(() => {
        if (!submitted || !redirectTo) return;

        const seconds = Math.max(redirectWaitSeconds ?? REDIRECT_DEFAULT_SECONDS, 0);
        const timeout = window.setTimeout(() => window.location.replace(redirectTo), seconds * 1000);

        return () => window.clearTimeout(timeout);
    }, [submitted, redirectTo, redirectWaitSeconds]);

    const frameProps = {
        theme: payload?.theme,
        reserveProgressSpace: paged,
        language,
        footer: payload && language
            ? (
                <PublicSurveyFooter
                    survey={payload}
                    localize={localize}
                    language={language}
                    organizationName={payload.organizationName}
                    languages={payload.languages}
                    onLanguageChange={chooseLanguage}
                />
            )
            : undefined,
    };

    switch (phase.kind) {
        case 'loading':
            return (
                <PublicSurveyFrame>
                    <PublicSurveyNotice busy/>
                </PublicSurveyFrame>
            );

        case 'unavailable': {
            const gone = phase.failure.kind === 'GONE';
            const missing = phase.failure.kind === 'NOT_FOUND';

            return (
                <PublicSurveyFrame>
                    <PublicSurveyNotice
                        title={gone
                            ? t('This link is no longer active')
                            : missing
                                ? t('This survey could not be found')
                                : t('Something went wrong')}
                        description={gone
                            ? t('The link was replaced or the survey stopped collecting answers. Please ask your host for a new one.')
                            : missing
                                ? t('Please check the link you were given, or ask your host for a new one.')
                                : t('The survey could not be loaded. Please try again in a moment.')}
                    />
                </PublicSurveyFrame>
            );
        }

        case 'alreadyAnswered':
            return (
                <PublicSurveyFrame {...frameProps}>
                    <PublicSurveyNotice
                        title={t('You have already answered this survey')}
                        description={t('Thank you - your answers were received. There is nothing left to do here.')}
                    />
                </PublicSurveyFrame>
            );

        case 'linkGone':
            return (
                <PublicSurveyFrame {...frameProps}>
                    <PublicSurveyNotice
                        title={t('This link is no longer active')}
                        description={t('Your answers could not be sent. Please ask your host for a new link.')}
                    />
                </PublicSurveyFrame>
            );

        case 'submitted':
            return (
                <PublicSurveyFrame
                    {...frameProps}
                    progress={paged ? surveyProgress(shown.length, shown.length - 1, 1, true) : undefined}
                >
                    <Stack gap={1} sx={{textAlign: 'center', py: {xs: 4, sm: 6}}}>
                        <SurveySuccessAnimation/>

                        <SurveyRunnerScreenTitle title={localize(endScreen?.title) || t('Thank you')}/>
                        {!!localize(endScreen?.description) && (
                            <SurveyRunnerMarkdown markdown={localize(endScreen?.description)} fontSize={16} color={'text.secondary'} align={'center'}/>
                        )}

                        {!!redirectTo && (
                            <Typography variant={'body2'} color={'text.secondary'} sx={{mt: 1}}>
                                {t('You are being redirected in a moment.')}
                            </Typography>
                        )}
                    </Stack>
                </PublicSurveyFrame>
            );

        case 'answering': {
            if (paged && welcome && !started) {
                return (
                    <PublicSurveyFrame {...frameProps}>
                        <Stack gap={2} alignItems={'center'} sx={{textAlign: 'center', py: {xs: 2, sm: 4}}}>
                            <Stack gap={1}>
                                <SurveyRunnerScreenTitle title={localize(welcome.title)}/>
                                {!!localize(welcome.description) && (
                                    <SurveyRunnerMarkdown markdown={localize(welcome.description)} fontSize={16} color={'text.secondary'} align={'center'}/>
                                )}
                            </Stack>
                            <Button
                                variant={'contained'}
                                size={'large'}
                                disableElevation
                                onClick={() => {
                                    setStarted(true);
                                    setActed(true);
                                }}
                                sx={{mt: 1, minHeight: 48, minWidth: 200}}
                            >
                                {localize(welcome.buttonLabel) || t('Start')}
                            </Button>
                        </Stack>
                    </PublicSurveyFrame>
                );
            }

            const submitting = phase.submit === 'sending';

            const card = (question: SurveyQuestion, separated: boolean, headed: boolean) => (
                <Box
                    key={question.id}
                    ref={(element: HTMLDivElement | null) => {
                        if (question.id) cardRefs.current[question.id] = element;
                    }}
                >
                    <SurveyRunnerQuestionCard
                        question={question}
                        localize={localize}
                        language={language ?? ''}
                        value={question.id ? answers[question.id] : undefined}
                        onAnswerChange={draft => question.id && setAnswer(question.id, draft)}
                        services={services}
                        error={messageFor(question)}
                        hint={hintFor(question)}
                        showFooter
                        titleLevel={headed ? 3 : undefined}
                        separated={separated}
                    />
                </Box>
            );

            const emptyState = (
                <Typography variant={'body2'} color={'text.secondary'}>
                    {t('This survey has no questions for you right now.')}
                </Typography>
            );

            /*
                Only One section per page pages carry a title. It is read from the page, not from its first visible
                question, so a hidden opening question keeps its section's heading; the question titles drop a
                level below it.
            */
            const sectionTitle = localize(currentPage?.sectionTitle);
            const headed = !!sectionTitle.trim();

            const pageContent = currentPage
                ? (
                    <>
                        {headed && <SurveyRunnerSectionHeading title={sectionTitle}/>}
                        {currentPage.questions.map((question, index) => card(question, index > 0, headed))}
                    </>
                )
                : emptyState;

            const submitLabel = localize(endScreen?.buttonLabel) || t('Submit');
            const onLastPage = currentIndex >= shown.length - 1;
            const answeredShare = currentPage
                ? currentPage.questions.filter(question => !!question.id && isAnswered(answers[question.id])).length
                    / currentPage.questions.length
                : 0;

            const submitButton = (inPagedNav?: boolean) => (
                <Button
                    variant={'contained'}
                    size={'large'}
                    disableElevation
                    disabled={submitting}
                    onClick={() => void submit()}
                    startIcon={submitting ? <CircularProgress size={16} color={'inherit'}/> : undefined}
                    sx={{minHeight: 48, ...(inPagedNav ? {minWidth: 140} : {alignSelf: 'flex-start'})}}
                >
                    {submitLabel}
                </Button>
            );

            return (
                <PublicSurveyFrame
                    {...frameProps}
                    /*
                        How far the guest HAS come, not how far into the survey the page they are looking at
                        sits: the pages behind them, plus the share of this one they have answered. The bar
                        once filled to 100% before the last question was answered, and then the thank-you
                        screen followed with nothing left to show - only the end screen is full.
                    */
                    progress={paged && currentPage
                        ? surveyProgress(shown.length, currentIndex, answeredShare, false)
                        : undefined}
                    showLogo={!paged}
                >
                    {!paged && !!welcome && (
                        <SurveyRunnerIntroCard
                            title={localize(welcome.title)}
                            description={localize(welcome.description)}
                        />
                    )}

                    {paged
                        ? (
                            <>
                                {pageContent}

                                <Stack direction={'row'} gap={2} justifyContent={'space-between'} alignItems={'center'}>
                                    <Button
                                        variant={'outlined'}
                                        size={'large'}
                                        disableElevation
                                        disabled={submitting || (currentIndex <= 0 && !welcome)}
                                        onClick={goBack}
                                        sx={{minHeight: 48}}
                                    >
                                        {t('Back')}
                                    </Button>

                                    {onLastPage
                                        ? submitButton(true)
                                        : (
                                            <Button
                                                variant={'contained'}
                                                size={'large'}
                                                disableElevation
                                                disabled={submitting}
                                                onClick={goNext}
                                                sx={{minHeight: 48, minWidth: 140}}
                                            >
                                                {t('Continue')}
                                            </Button>
                                        )}
                                </Stack>
                            </>
                        )
                        : (
                            <>
                                {pageContent}
                                {submitButton()}
                            </>
                        )}

                    {phase.submit === 'failed' && (
                        <Typography variant={'body2'} color={'error.main'} role={'alert'}>
                            {t('Your answers could not be sent. Please try again in a moment.')}
                        </Typography>
                    )}
                </PublicSurveyFrame>
            );
        }
    }
}

export default function PublicSurveyPage() {
    const {surveyId, token} = useParams();
    return <PublicSurvey key={token ?? surveyId} surveyId={surveyId} token={token}/>;
}
